import React, { useMemo, useState } from 'react';
import WithSection from '../../components/WithSection/WithSection';
import { graphql } from 'gatsby';
import Box from '../../components/Box/Box';
import { Body1, Head2, Head6 } from '../../components/Typography/Typography';
import Image from '../../components/Image/Image';
import { isLightColor } from '../../utils/isLightColor';
import { rem } from 'polished';
import Stack from '../../components/Stack/Stack';
import InputSlots, { normalizeValues } from '../../slots/InputSlots/InputSlots';
import { useForm } from 'react-hook-form';
import { submitForm } from '../../services/externalForm';
import Button from '../../components/Button/Button';
import { theme } from '../../../config/theme';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import { track } from '../../utils/analytics';
import RichText from '../../components/RichText/RichText';

const formWrapperStyles = {
    alignSelf: 'start',
    bg: 'neutral.white',
    borderRadius: 'sm',
    p: rem(20),
    boxShadow: 'primary-thin',
};

const SurveyReportSection = ({
    data,
    styling: { backgroundVariant: sectionBackground },
}) => {
    const {
        primary: { form, section_title, section_description, image },
    } = data;
    const {
        submit_url,
        cta_text,
        cta_tracking_event,
        fields,
        success_image,
        success_msg,
        success_heading,
        success_cta_text,
        success_cta_link,
    } = form.document.data;
    const isDarkBackground = !isLightColor(sectionBackground);
    const { handleSubmit, register, errors, control } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const onSubmit = (values) => {
        setIsSubmitting(true);
        const normalizedValues = normalizeValues(values);
        submitForm({ url: submit_url, data: normalizedValues })
            .then((res) => {
                setIsSubmitted(true);
                if (cta_tracking_event)
                    track(cta_tracking_event, {
                        companyName: normalizedValues.company,
                    });
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <Box
                display="grid"
                gridGap={rem(16)}
                gridColumn={{ _: '1/-1', md: '2/-2', lg: '2/span 5' }}
                maxWidth={{ _: 'initial', lg: rem(466) }}
            >
                <Stack space={rem(8)} maxWidth={{ _: 'initial', md: rem(466) }}>
                    <Box py={rem(8)}>
                        <RichText render={section_title.richText} />
                    </Box>

                    <Body1
                        py={rem(8)}
                        {...(isDarkBackground && { color: 'neutral.white' })}
                    >
                        {section_description.text}
                    </Body1>
                </Stack>
                {isSubmitted ? (
                    <Stack space={rem(20)} {...formWrapperStyles}>
                        <Box
                            textAlign="center"
                            bg="neutral.white"
                            borderRadius="sm"
                            overflow="hidden"
                        >
                            <Image
                                img={success_image}
                                backgroundColor="neutral.white"
                            />
                        </Box>
                        <Head6>{success_heading}</Head6>
                        <Body1>{success_msg}</Body1>
                        <Box py={rem(8)}>
                            <PrismicLink link={success_cta_link}>
                                <Button
                                    size="lg"
                                    fullWidth
                                    loading={isSubmitting}
                                >
                                    {success_cta_text}
                                </Button>
                            </PrismicLink>
                        </Box>
                    </Stack>
                ) : (
                    <Stack
                        as="form"
                        onSubmit={handleSubmit(onSubmit)}
                        space={rem(20)}
                        noValidate
                        {...formWrapperStyles}
                    >
                        <InputSlots
                            inputs={fields}
                            extendProps={({
                                key,
                                is_required,
                                pattern,
                                label,
                            }) => ({
                                ref: register({
                                    required: is_required,
                                    pattern,
                                }),
                                error: errors[key],
                                control,
                                label,
                            })}
                        />
                        <Box py={rem(8)}>
                            <Button size="lg" fullWidth loading={isSubmitting}>
                                {cta_text}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </Box>
            <Box
                gridColumn={{
                    _: '1/-1',
                    md: '2/-2',
                    lg: '7 / span 5',
                }}
                textAlign="center"
                mt={{ _: rem(16), lg: 'initial' }}
            >
                <Image img={image} />
            </Box>
        </>
    );
};

export default WithSection(SurveyReportSection);

export const _ = graphql`
    fragment SURVEY_REPORT_SECTION on PrismicSectionDataBodySurveyReportSection {
        id
        primary {
            section_title {
                richText
            }
            section_description {
                text
            }
            image {
                gatsbyImageData(height: 512, layout: CONSTRAINED)
            }
            form {
                type
                id
                document {
                    ... on PrismicDynamicForm {
                        id
                        data {
                            cta_text
                            cta_tracking_event
                            fields {
                                key
                                is_required
                                label
                                options
                                placeholder
                                type
                            }
                            submit_url
                            success_msg
                            success_heading
                            success_cta_text
                            success_cta_link {
                                ...PRISMIC_LINK_FRAGMENT
                            }
                            success_image {
                                gatsbyImageData(
                                    height: 305
                                    layout: CONSTRAINED
                                )
                            }
                        }
                    }
                }
            }
        }
    }
`;
